import { Link, useLocation } from "react-router-dom";
import { useAuthStore } from "../store/useAuthStore";
import {
  LogOut,
  Settings,
  User,
  Clock,
  Bell,
  Search,
  Command,
} from "lucide-react";
import { roleLabel, roleBadgeClass } from "../lib/role";
import { useAttendanceStore } from "../store/useAttendanceStore";
import { useActivityStore } from "../store/useActivityStore";
import { useEffect } from "react";

const PAGE_TITLES: Record<string, { title: string; sub?: string }> = {
  "/chat": { title: "Conversation", sub: "Client relationship messages" },
  "/clients": { title: "Contacts", sub: "Manage your clients" },
  "/projects": { title: "Projects", sub: "Active engagements" },
  "/questionnaires": { title: "Questionnaires", sub: "Discovery & intake forms" },
  "/proposals": { title: "Proposals", sub: "Reusable proposal templates" },
  "/proposal-builder": { title: "Proposal Builder", sub: "Compose a new proposal" },
  "/bids": { title: "My Bids", sub: "Upwork bids & connects tracker" },
  "/upwork-jobs": { title: "Upwork Jobs", sub: "AI-scored opportunities" },
  "/profile": { title: "Profile", sub: "Account settings" },
  "/finance": { title: "Finance", sub: "Account settings" },
  "/settings": { title: "Settings", sub: "Organization-wide system settings & business rules" },
};

type PageTitle = { title: string; sub?: string };

const resolveTitle = (pathname: string): PageTitle => {

  const exact = PAGE_TITLES[pathname];
  if (exact) return exact;

  const hitKey = Object.keys(PAGE_TITLES).find(
    (k) => k !== "/" && pathname.startsWith(k)
  );
  return (hitKey ? PAGE_TITLES[hitKey] : undefined) ?? { title: "Dashboard" };
};

const Navbar = () => {
  const { logout, authUser } = useAuthStore();
  const { clockedIn, loading, checkStatus, timeIn, timeOut } =
    useAttendanceStore();
  const { startTracking, stopTracking } = useActivityStore();
  const location = useLocation();

  const isFinanceUser = authUser?.role === "Finance User";

  useEffect(() => {
    if (authUser && !isFinanceUser) checkStatus();
  }, [authUser, isFinanceUser, checkStatus]);

  const handleTimeIn = async () => {
    await timeIn();
    await startTracking();
  };
  const handleTimeOut = async () => {
    await timeOut();
    await stopTracking();
  };

  const page = resolveTitle(location.pathname);

  const initials = (authUser?.fullName || authUser?.email || "U")
    .split(" ")
    .map((s: string) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <header className="hidden lg:block fixed top-0 left-56 2xl:left-64 right-0 z-40 h-16 border-b border-slate-200/70 bg-white/85 backdrop-blur-xl">
      <div className="h-full flex items-center justify-between pl-6 2xl:pl-8 pr-6 gap-4">

        <div className="min-w-0 flex-shrink-0">
          <h1 className="text-[15px] font-bold text-slate-800 tracking-tight leading-tight truncate">
            {page.title}
          </h1>
          {page.sub && (
            <p className="text-[11px] text-slate-400 truncate">{page.sub}</p>
          )}
        </div>

        <div className="hidden lg:flex flex-1 max-w-md">
          <button className="w-full flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-lg bg-slate-100/70 hover:bg-slate-100 ring-1 ring-slate-200/70 text-slate-400 hover:text-slate-600 transition text-sm group">
            <Search size={14} />
            <span className="flex-1 text-left text-[13px]">
              Quick search…
            </span>
            <span className="flex items-center gap-0.5 text-[10px] font-semibold text-slate-400 bg-white ring-1 ring-slate-200 rounded px-1.5 py-0.5">
              <Command size={10} />K
            </span>
          </button>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">

          <button
            className="relative p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100/80 transition"
            title="Notifications"
          >
            <Bell size={17} />
            <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-red-500 rounded-full ring-2 ring-white animate-pulse" />
          </button>

          <Link
            to="#"
            className="p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100/80 transition"
            title="Settings"
          >
            <Settings size={17} />
          </Link>

          <div className="w-px h-6 bg-slate-200 mx-1" />

          {authUser && (
            <>

              <Link
                to="/profile"
                className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-lg hover:bg-slate-100/80 transition group"
                title={authUser.fullName || "Profile"}
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#03488A] to-[#00AAF1] grid place-items-center text-white text-[11px] font-bold shadow-sm ring-2 ring-white">
                  {initials || <User size={14} />}
                </div>
                <div className="hidden sm:flex flex-col leading-tight">
                  <span className="text-[12px] font-bold text-slate-800 truncate max-w-[110px]">
                    {authUser.fullName || "Profile"}
                  </span>
                  <span className={`text-[10px] font-bold px-1.5 py-px rounded-full ring-1 w-fit ${roleBadgeClass(authUser.role)}`}>
                    {roleLabel(authUser.role)}
                  </span>
                </div>
              </Link>

              <button
                onClick={logout}
                className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition"
                title="Logout"
              >
                <LogOut size={17} />
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
